import React from 'react';
import { uniqueId } from 'lodash';
import { FieldArrayRenderProps, Field, FieldProps, useFormikContext } from 'formik';
import { FormGroupLabel, Dropdown, TextField, Button, Box } from '@qbe/ads-core';
import { FormCard } from '@qbe/ads-blocks-forms';
import { useContent, useContentContext } from '@qbe/dfc-content';

import CurrencyTextField from 'components/CurrencyTextField/CurrencyTextField';
import FormSpacer from 'components/FormSpacer/FormSpacer';
import { incomeTypeList, IncomeFormFields, IncomeData } from 'contexts/FormProvider';
import { incomeValidationSchema } from '../schema';

interface Props {
  arrayHelpers: FieldArrayRenderProps;
  index: number;
}

const IncomeEdit = React.forwardRef((props: Props, ref: React.Ref<HTMLDivElement>) => {
  const { arrayHelpers, index } = props;

  const { values } = useFormikContext<IncomeFormFields>();

  const { getTextContent } = useContent({
    context: useContentContext(),
  });

  const onAddIncomeDetails = (idx: number) => {
    const newDetails: IncomeData = {
      id: uniqueId(),
      incomeType: '',
      incomeDescription: '',
      incomeAmount: '',
    };

    arrayHelpers.insert(idx + 1, newDetails);
  };

  const isDisabled = !incomeValidationSchema.isValidSync(values);
  const hasIncome = values.incomeStatus === 'yes';
  const isFull = values.incomeData.length === 5;
  const hasOneElement = values.incomeData.length === 1;
  // Localized content constants
  const sectionHeadingBase = getTextContent('Income.details.editHeading');
  const incomeTypeTitle = getTextContent('Income.details.incomeType');
  const incomeTypeLabel = getTextContent('Income.details.incomeType.label');
  const incomeDescriptionTitle = getTextContent('Income.details.incomeDescription');
  const incomeDescriptionLabel = getTextContent('Income.details.incomeDescription.label');
  const incomeAmountTitle = getTextContent('Income.details.incomeAmount');
  const incomeAmountLabel = getTextContent('Income.details.incomeAmount.label');
  const addIncomeLabel = getTextContent('Income.details.addIncome.label');
  const removeIncomeLabel = getTextContent('Income.details.removeIncome.label');

  if (!hasIncome) return null;

  return (
    <>
      <FormSpacer type="pagesection" />
      <div ref={ref} />
      <FormCard variant="sectionTitle" title={`${sectionHeadingBase} - (${index + 1})`}>
        <FormGroupLabel title={incomeTypeTitle} required alignCenter />
        <FormSpacer type="input" />
        <Field name={`incomeData[${index}].incomeType`}>
          {({ field, meta }: FieldProps) => (
            <Dropdown
              {...field}
              id={field.name}
              label={incomeTypeLabel}
              data-track={field.name}
              data-track-value="capture"
              optionList={incomeTypeList.map((i) => ({ label: i, value: i }))}
              onChange={(event) => field.onChange(event)}
              error={meta.touched && !!meta.error}
            />
          )}
        </Field>

        <FormSpacer type="formgroup" />

        <FormGroupLabel title={incomeDescriptionTitle} required alignCenter />
        <FormSpacer type="input" />
        <Field name={`incomeData[${index}].incomeDescription`}>
          {({ field, meta }: FieldProps) => (
            <TextField
              {...field}
              id={field.name}
              multiline
              label={incomeDescriptionLabel}
              maxLength={225}
              data-track="incomeDescription"
              data-track-value="mask"
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
            />
          )}
        </Field>

        <FormSpacer type="formgroup" />

        <FormGroupLabel title={incomeAmountTitle} required alignCenter />
        <FormSpacer type="input" />
        <Field name={`incomeData[${index}].incomeAmount`}>
          {({ field, meta }: FieldProps) => (
            <CurrencyTextField
              {...field}
              id={field.name}
              label={incomeAmountLabel}
              data-track={incomeAmountLabel}
              data-track-value="mask"
              maxLength={10}
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
            />
          )}
        </Field>

        <FormSpacer type="formgroup" />

        <Box display="flex" flexDirection="row" justifyContent="center">
          <Button
            type="button"
            label={addIncomeLabel}
            variant="secondary-on-light"
            disabled={isFull || isDisabled}
            onClick={() => onAddIncomeDetails(index)}
          />
          <FormSpacer type="input" direction="row" />
          <Button
            type="button"
            label={removeIncomeLabel}
            data-track={removeIncomeLabel}
            variant="secondary-on-light"
            disabled={hasOneElement}
            onClick={() => arrayHelpers.remove(index)}
          />
        </Box>
      </FormCard>
    </>
  );
});

IncomeEdit.displayName = 'IncomeEdit';

export default IncomeEdit;
